/**
 * Klartext für die Datenbankschlüssel der Außenanlagen — Kreise, Bauarten,
 * Bereiche, Bewertungen, Herkünfte, Nachweisstände, Richtungen, Gewerke.
 *
 * ════════════════════════════════════════════════════════════════════════
 * WARUM ES DIESE DATEI GIBT
 * ════════════════════════════════════════════════════════════════════════
 *
 * Bis zum 23.09.2026 zeigten die vier Außenseiten die Schlüssel selbst: in
 * der Spalte *Kreis* stand `trinkwasser`, in der Spalte *Bauart*
 * `ueberflur`, in der Bewertungsspalte `nicht_pruefbar`. Der Fachplaner hat
 * es an drei Bildschirmausschnitten gemeldet.
 *
 * Vier Teillisten gab es schon — `KREISNAMEN` in `Aussenanlagen.tsx`,
 * `KREISNAMEN` noch einmal in `AussenNachweis.tsx` (mit DREI Einträgen
 * statt sieben), `BAUARTEN` daneben, `ART`/`Artname` in
 * `AussenGeraete.tsx`. Vier Listen, die nichts voneinander wissen, sind
 * schlechter als eine: Sie sehen aus wie eine Übersetzung und sind eine
 * Stichprobe.
 *
 * ════════════════════════════════════════════════════════════════════════
 * EIN WORT, DAS SCHÖNER KLINGT, DARF NICHTS ANDERES BEDEUTEN
 * ════════════════════════════════════════════════════════════════════════
 *
 * `obere_schranke` heißt „obere Schranke" und nicht „Ergebnis";
 * `nicht_pruefbar` heißt „nicht prüfbar" und nicht „ok";
 * `erfuellt_mit_massnahme` trägt die Maßnahme im Wort mit.
 *
 * ════════════════════════════════════════════════════════════════════════
 * EIN UNBEKANNTER SCHLÜSSEL BLEIBT SICHTBAR
 * ════════════════════════════════════════════════════════════════════════
 *
 * Der Rückfall ist NICHT der leere Text und nicht „—", sondern der
 * Schlüssel selbst mit Unterstrichen als Leerzeichen und großem
 * Anfangsbuchstaben: aus `neue_bewertung` wird „Neue bewertung".
 *
 * ════════════════════════════════════════════════════════════════════════
 * GESPIEGELT AUS DER API
 * ════════════════════════════════════════════════════════════════════════
 *
 * Zwei Laufzeiten, kein gemeinsamer Code — die Tabellen stehen deshalb ein
 * zweites Mal in `src/Tuv.Export.Api/Services/AussenKlartext.cs`. Ändert
 * sich ein Wert hier, muss er dort mitgezogen werden, und umgekehrt.
 * `alleSchluessel` ist die Liste, gegen die `aussenKlartext.test.ts` zählt.
 */

/** Aus `neue_bewertung` wird „Neue bewertung". */
export function rueckfall(schluessel: string): string {
  if (schluessel.length === 0) return schluessel;

  const text = schluessel.replace(/_/g, ' ');

  return text.charAt(0).toUpperCase() + text.slice(1);
}

function nachschlagen(
  schluessel: string | null | undefined,
  tabelle: Record<string, string>,
  leer = '—',
): string {
  if (schluessel == null || schluessel.trim() === '') return leer;

  return tabelle[schluessel] ?? rueckfall(schluessel);
}

// --- Die Kreise -----------------------------------------------------------
//
// Der Kreis ist die Bemessungseinheit: Regen- und Schmutzwasser liegen im
// selben Graben und werden getrennt gerechnet.
const kreisTabelle: Record<string, string> = {
  regenwasser: 'Regenwasser',
  schmutzwasser: 'Schmutzwasser',
  mischwasser: 'Mischwasser',
  fettabwasser: 'Fettabwasser',
  trinkwasser: 'Trinkwasser',
  loeschwasser: 'Löschwasser',
  kondensat: 'Kondensat',
  drainage: 'Drainage',
  druckleitung: 'Druckleitung',
  leerrohr: 'Leerrohr',
  fernwaerme: 'Fernwärme',
  fernwaerme_vorlauf: 'Fernwärme Vorlauf',
  fernwaerme_ruecklauf: 'Fernwärme Rücklauf',
};

export const kreisname = (s: string | null | undefined): string =>
  nachschlagen(s, kreisTabelle);

/**
 * Trägt dieser Kreis Druck — oder läuft er im Freispiegel?
 *
 * ════════════════════════════════════════════════════════════════════════
 * WARUM DAS EINE ANZEIGEFRAGE IST
 * ════════════════════════════════════════════════════════════════════════
 *
 * Am 23.09.2026 stand in der Leitungsliste bei den Trinkwasserleitungen
 * *J [‰]* = `0,0` und daneben in der Spalte *Gefälle* ein „—". Das las sich
 * wie eine misslungene Rechnung. Gemessen an der Bestandskopie ist es
 * beides nicht:
 *
 *   102 Trinkwasserrohre tragen `GefaelleHerkunft = 'geometrie'`, 68 davon
 *   ein Gefälle von exakt 0 — sie liegen waagerecht, und das ist gemessen.
 *   Ein Mindestgefälle trägt KEINES von ihnen, weil es für eine
 *   Druckleitung keines gibt.
 *
 * Die Null ist also richtig und die Prüfspalte zu Recht leer. Falsch war
 * nur, dass beides aussah wie eine Lücke. Deshalb sagt die Prüfspalte
 * jetzt, WARUM sie leer ist — und dafür muss die Seite wissen, welcher
 * Kreis unter Druck steht.
 */
export function istDruckkreis(kreis: string | null | undefined): boolean {
  return kreis != null && [
    'trinkwasser', 'loeschwasser', 'druckleitung',
    'fernwaerme', 'fernwaerme_vorlauf', 'fernwaerme_ruecklauf',
  ].includes(kreis);
}

// --- Die Bauarten ---------------------------------------------------------
//
// Schächte, Hydranten und Versickerungsbauwerke tragen sie in derselben
// Spalte. `ueberflur` ist eine Kennung und keine Bezeichnung.
const bauartTabelle: Record<string, string> = {
  ueberflur: 'Überflurhydrant',
  unterflur: 'Unterflurhydrant',
  schacht: 'Schacht',
  polycarbonatschacht: 'Polycarbonatschacht',
  pumpwerk: 'Abwasserpumpwerk',
  pumpstation: 'Pumpstation',
  fettabscheider: 'Fettabscheider',
  rigole: 'Rigole',
  mulde: 'Versickerungsmulde',
  versickerungsflaeche: 'Versickerungsfläche',
  zisterne: 'Zisterne',
  rueckhaltebecken: 'Rückhaltebecken',
  rueckhaltung: 'Rückhaltung',
  hydrant: 'Hydrant',
};

export const bauartname = (s: string | null | undefined): string =>
  nachschlagen(s, bauartTabelle);

// --- Die Bereiche ---------------------------------------------------------
//
// SIEBEN und nicht sechs: `verlegetiefe` wurde immer mitgerechnet und stand
// in keiner Auswahlliste.
const bereichTabelle: Record<string, string> = {
  freispiegel: 'Freispiegelentwässerung',
  loeschwasser: 'Löschwasserversorgung',
  fernwaerme: 'Fernwärme',
  versickerung: 'Versickerung und Rückhaltung',
  trinkwasser: 'Trinkwasseranschluss',
  leerrohr: 'Leerrohrnetz',
  verlegetiefe: 'Verlegetiefe und Frostgrenze',
};

export const bereichname = (s: string | null | undefined): string =>
  nachschlagen(s, bereichTabelle);

// --- Die Bewertungen ------------------------------------------------------
//
// Drei Gruppen, die verschieden viel behaupten:
//
//   ERFÜLLT  — `ok`, `erfuellt`, `frostfrei`: Der Nachweis ist geführt.
//   LÜCKE    — `ohne_abfluss`, `nicht_pruefbar`: Es wurde NICHT geprüft.
//              238 der 282 Freispiegel-Teilstrecken stehen so da.
//   MANGEL   — `gefaelle_zu_klein`, `ueberlastet`: geprüft, nicht erfüllt.
//
// `erfuellt_mit_massnahme` trägt die Maßnahme im Wort: Der Nachweis gelingt
// nur, WENN etwas gebaut wird.
const bewertungTabelle: Record<string, string> = {
  // erfüllt
  ok: 'in Ordnung',
  erfuellt: 'erfüllt',
  erfuellt_mit_massnahme: 'erfüllt mit Maßnahme',
  frostfrei: 'frostfrei',
  ohne_frostgefahr: 'ohne Frostgefahr',
  regulaer: 'regulär',

  // Nachweislücke: NICHT geprüft
  ohne_abfluss: 'ohne Bemessungsabfluss',
  ohne_gefaelle: 'ohne Gefälle',
  ohne_hoehe: 'ohne Höhenangabe',
  ohne_dauerstufen: 'ohne Dauerstufen',
  nicht_pruefbar: 'nicht prüfbar',
  randbedingung: 'Randbedingung',
  befund: 'Befund',
  modellpflege: 'Modellpflege',
  abseits: 'abseits des Netzes',
  ohne_schacht: 'ohne Schacht',
  weg_unvollstaendig: 'Weg unvollständig',
  stich: 'Stichleitung',
  bergauf: 'bergauf',

  // Mangel: geprüft und nicht erfüllt
  beanstandet: 'beanstandet',
  nicht_erfuellt: 'nicht erfüllt',
  waagerecht: 'waagerecht verlegt',
  gefaelle_zu_klein: 'Gefälle zu klein',
  zu_flach: 'zu flach verlegt',
  fuellungsgrad_hoch: 'Füllungsgrad zu hoch',
  geschwindigkeit_hoch: 'Fließgeschwindigkeit zu hoch',
  geschwindigkeit_niedrig: 'Fließgeschwindigkeit zu niedrig',
  ueberlastet: 'überlastet',
  unterdeckt: 'unterdeckt',
  volumen_doppelt: 'Volumen doppelt erfasst',
  zu_klein: 'zu klein',
  abstand_zu_gross: 'Abstand zu groß',
  druckverlust_hoch: 'Druckverlust zu hoch',
  durchmesser_unplausibel: 'Durchmesser unplausibel',
  abweichung: 'Abweichung',
  drossel_offen: 'Drossel offen',
  verletzt: 'verletzt',
  hoch_gegen_netz: 'hoch gegen das Netz',
  tief_gegen_netz: 'tief gegen das Netz',
};

export const bewertungname = (s: string | null | undefined): string =>
  nachschlagen(s, bewertungTabelle);

/**
 * Wie eine Bewertung zu FÄRBEN ist — erfüllt, Lücke oder Mangel.
 *
 * Eine Nachweislücke ist NEUTRAL und nicht rot: Wer `ohne_abfluss` rot
 * färbt, färbt 238 von 282 Teilstrecken rot und findet die neun echten
 * Gefällebefunde nicht mehr.
 */
export function bewertungzustand(
  s: string | null | undefined,
): 'gut' | 'neutral' | 'schlecht' {
  if (s == null || s === '') return 'neutral';
  if (['ok', 'erfuellt', 'frostfrei', 'ohne_frostgefahr', 'regulaer']
    .includes(s)) return 'gut';
  if (['ohne_abfluss', 'ohne_gefaelle', 'ohne_hoehe', 'ohne_dauerstufen',
    'nicht_pruefbar', 'randbedingung', 'befund', 'modellpflege', 'abseits',
    'ohne_schacht', 'weg_unvollstaendig', 'stich', 'bergauf']
    .includes(s)) return 'neutral';
  return 'schlecht';
}

// --- Die Herkünfte --------------------------------------------------------
//
// Die Herkunft sagt, was eine Zahl wert ist. `obere_schranke` heißt, dass
// der WAHRE Wert kleiner ist — wer daraus „Ergebnis" macht, hat aus einer
// Abschätzung einen Nachweis gemacht.
const herkunftTabelle: Record<string, string> = {
  modell: 'Modell',
  abgeleitet: 'abgeleitet',
  gepflegt: 'gepflegt',
  hand: 'von Hand',
  berechnet: 'berechnet',
  gerechnet: 'gerechnet',
  angenommen: 'angenommen',
  ueblich: 'üblicher Wert',
  offen: 'offen',
  festlegung: 'Festlegung des Fachplaners',
  auftrag: 'Festlegung des Bauherrn',
  norm: 'Norm',
  datenblatt: 'Datenblatt',
  ungueltig: 'ungültig',

  obere_schranke: 'obere Schranke',
  untere_schranke: 'untere Schranke',

  regel: 'Kreisregel',
  regel_entfallen: 'Kreisregel entfallen',
  nachbarschaft: 'aus der Nachbarschaft',
  umfeldsuche: 'aus der Umfeldsuche',
  systemname_geparst: 'aus dem Systemnamen gelesen',
  typname: 'aus dem Typnamen gelesen',
  geometrie: 'aus der Geometrie',
  typname_geparst: 'aus dem Typnamen gelesen',
  planangabe: 'Planangabe',
  bodengutachten: 'Bodengutachten',
  produkt: 'Produktdatenblatt',

  // TS9b2 (Reiter Rückhaltung): die Herkünfte an `aussen_rueckhaltungen`.
  offen_wasserbehoerde: 'offen — Zustimmung der Wasserbehörde',
  genehmigung: 'Genehmigung',
  nicht_im_bestand: 'nicht im Bestand',
};

export const herkunftname = (s: string | null | undefined): string =>
  nachschlagen(s, herkunftTabelle);

// --- Die Nachweisstände ---------------------------------------------------
const nachweisstandTabelle: Record<string, string> = {
  nachgewiesen: 'nachgewiesen',
  ohne_nachweis: 'ohne Nachweis',
  offen_wasserbehoerde: 'offen — Zustimmung der Wasserbehörde',
  nicht_pruefbar: 'nicht prüfbar',
  nicht_erforderlich: 'nicht erforderlich',
};

export const nachweisstandname = (s: string | null | undefined): string =>
  nachschlagen(s, nachweisstandTabelle);

// --- Richtung, Gewerk, Bauteilart -----------------------------------------
const richtungTabelle: Record<string, string> = {
  zulauf: 'Zulauf',
  ablauf: 'Ablauf',
  unbekannt: 'Richtung unbekannt',
};

export const richtungname = (s: string | null | undefined): string =>
  nachschlagen(s, richtungTabelle);

const gewerkTabelle: Record<string, string> = {
  sanitaer: 'Sanitär',
  elektro: 'Elektro',
  heizung: 'Heizung',
  aussen: 'Außenanlagen',
};

export const gewerkname = (s: string | null | undefined): string =>
  nachschlagen(s, gewerkTabelle);

const seiteTabelle: Record<string, string> = {
  vorlauf: 'Vorlauf',
  ruecklauf: 'Rücklauf',
};

export const seitenname = (s: string | null | undefined): string =>
  nachschlagen(s, seiteTabelle);

const artTabelle: Record<string, string> = {
  rohr: 'Rohr',
  formteil: 'Formteil',
  leerrohr: 'Leerrohr',
  hydrant: 'Hydrant',
  schacht: 'Schacht',
  strecke: 'Strecke',
  anlage: 'Anlage',
  strang: 'Strang',
  bauwerk: 'Bauwerk',
  befund: 'Befund',
  trasse: 'Trasse',
  netz: 'Netz',
  einleitstelle: 'Einleitstelle',
  zubehoer: 'Zubehör',
  geraet: 'Gerät',

  // Die Arten einer EINZUGSFLÄCHE — dieselbe Spalte „Art".
  dach: 'Dachfläche',
  verkehrsflaeche: 'Verkehrsfläche',
  sonstige: 'sonstige Fläche',
};

export const artname = (s: string | null | undefined): string =>
  nachschlagen(s, artTabelle);

/**
 * Alle Schlüssel je Gruppe — die Liste, gegen die gezählt wird.
 *
 * Sie muss Zeichen für Zeichen dieselbe sein wie
 * `AussenKlartext.AlleSchluessel` in der API.
 */
export const alleSchluessel: Record<string, string[]> = {
  kreis: Object.keys(kreisTabelle),
  bauart: Object.keys(bauartTabelle),
  bereich: Object.keys(bereichTabelle),
  bewertung: Object.keys(bewertungTabelle),
  herkunft: Object.keys(herkunftTabelle),
  nachweisstand: Object.keys(nachweisstandTabelle),
  richtung: Object.keys(richtungTabelle),
  gewerk: Object.keys(gewerkTabelle),
  art: Object.keys(artTabelle),
  seite: Object.keys(seiteTabelle),
};
